import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import { POrdersDto } from '../types/porders';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Punkt 17 (Norlux): én ufullstendig mottakslinje (bestilt > ankomst-registrert).
export interface IncompleteReceiptLine {
  orderNo: number;
  orderNoERP: string;
  lineNo: number;
  itemNo: string;
  description1: string | null;
  orderedQuantity: number;
  registeredQuantity: number;
  remainingQuantity: number;
  itemUnitTxt: string | null;
  lineStatus: number;
  orderStatus: number;
  supplierNo: string;
  supplierName: string | null;
  orderDate: string | null;
  warehouse: string | null;
}

// Én gammel ankommet-linje: StcTrans på LineStatus 20 eldre enn N dager, aldri fullført/slettet.
export interface StaleArrivedLine {
  stcTransId: number;
  batchID: string;
  orderNo: number;
  orderNoERP: string;
  lineNo: number;
  itemNo: string;
  description1: string | null;
  quantity: number;
  itemUnitTxt: string | null;
  locationName: string | null;
  warehouse: string | null;
  createdBy: string | null;
  created: string;
  daysOld: number;
  supplierNo: string;
  supplierName: string | null;
}

class POrdersService {
  
  /**
   * Henter alle porders for varemottak (OrderStatus < 60)
   */
  async getOrdersForReceiving(warehouse?: string, itemNo?: string): Promise<POrdersDto[]> {
    const params = new URLSearchParams();
    if (warehouse) params.append('warehouse', warehouse);
    if (itemNo) params.append('itemNo', itemNo);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/receiving${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get orders for receiving: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Punkt 17 (Norlux): Henter ufullstendige mottak — ordrelinjer der bestilt > ankomst-registrert.
   */
  async getIncompleteReceipts(filters?: {
    fromDate?: string;
    toDate?: string;
    supplierNo?: string;
    warehouse?: string;
  }): Promise<IncompleteReceiptLine[]> {
    const params = new URLSearchParams();
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.supplierNo) params.append('supplierNo', filters.supplierNo);
    if (filters?.warehouse) params.append('warehouse', filters.warehouse);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/incomplete-receipts${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get incomplete receipts: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter gamle ankommet-linjer — StcTrans på LineStatus 20 (Ankommet) eldre enn angitt
   * antall dager, som aldri ble fullført eller slettet.
   */
  async getStaleArrivedLines(filters?: {
    olderThanDays?: number;
    warehouse?: string;
  }): Promise<StaleArrivedLine[]> {
    const params = new URLSearchParams();
    if (filters?.olderThanDays !== undefined) params.append('olderThanDays', String(filters.olderThanDays));
    if (filters?.warehouse) params.append('warehouse', filters.warehouse);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/stale-arrived-lines${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get stale arrived lines: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter alle porders
   */
  async getAllOrders(warehouse?: string): Promise<POrdersDto[]> {
    const params = new URLSearchParams();
    if (warehouse) params.append('warehouse', warehouse);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get all orders: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter porders basert på status
   */
  async getOrdersByStatus(status: number, warehouse?: string): Promise<POrdersDto[]> {
    const params = new URLSearchParams();
    if (warehouse) params.append('warehouse', warehouse);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/status/${status}${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get orders by status ${status}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter en spesifikk porder
   */
  async getOrderByOrderNo(orderNo: number): Promise<POrdersDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/${orderNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get order ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter porders basert på leverandør
   */
  async getOrdersBySupplier(supplierNo: string, warehouse?: string): Promise<POrdersDto[]> {
    const params = new URLSearchParams();
    if (warehouse) params.append('warehouse', warehouse);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/supplier/${supplierNo}${queryString}`);

    if (!response.ok) {
      throw new Error(`Failed to get orders by supplier ${supplierNo}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Søker etter porders basert på ERP-ordrenummer
   */
  async searchOrdersByERPOrderNo(erpOrderNo: string, warehouse?: string): Promise<POrdersDto[]> {
    const params = new URLSearchParams();
    params.append('erpOrderNo', erpOrderNo);
    if (warehouse) params.append('warehouse', warehouse);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/search?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search orders by ERP order no: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter porders basert på datoperiode
   */
  async getOrdersByDateRange(fromDate: Date, toDate: Date, warehouse?: string): Promise<POrdersDto[]> {
    const fromDateStr = fromDate.toISOString().split('T')[0];
    const toDateStr = toDate.toISOString().split('T')[0];

    const params = new URLSearchParams();
    params.append('fromDate', fromDateStr);
    params.append('toDate', toDateStr);
    if (warehouse) params.append('warehouse', warehouse);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/daterange?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get orders by date range: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Oppdaterer status på en porder
   */
  async updateOrderStatus(orderNo: number, status: number): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/porders/${orderNo}/status`,
      { status }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update order status: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Henter order-statistikk
   */
  async getOrderStatistics(): Promise<any> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/statistics`);
    
    if (!response.ok) {
      throw new Error(`Failed to get order statistics: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Oppdaterer en ordre
   */
  async updateOrder(orderNo: number, order: Partial<POrdersDto>): Promise<POrdersDto> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/porders/${orderNo}`,
      order
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update order ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Sletter en bestilling og alle tilhørende linjer.
   * Kun tillatt for bestillinger med status < 20 (Ubehandlet eller Godkjent).
   */
  async deleteOrder(orderNo: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${API_BASE_URL}/porders/${orderNo}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke slette bestilling'));
    }
  }

  /**
   * Sletter en bestillingslinje.
   * Kun tillatt for bestillinger med status < 60 (ikke eksportert).
   */
  async deleteOrderLine(orderNo: number, lineNo: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${API_BASE_URL}/porders/${orderNo}/lines/${lineNo}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke slette bestillingslinje'));
    }
  }

  /**
   * Formaterer dato til norsk format
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('no-NO');
  }
}

export const pOrdersService = new POrdersService();
